import * as React from 'react'
import { Tooltip } from '@blueprintjs/core'
import { UprightStyle } from '../types'

// Determine whether the HTML element is currently overflowing its boundaries
// use in conjunction with `overflow: hidden`
const checkOverflow = (clientWidth, scrollWidth, clientHeight, scrollHeight, windowHeight, windowWidth) => {
  if (!clientWidth || !scrollWidth || !clientHeight || !scrollHeight || !windowHeight || !windowWidth) {
    return false
  }

  return clientWidth < scrollWidth || clientHeight < scrollHeight
}

interface WindowDimensions {
  windowWidth: number
  windowHeight: number
}

const useWindowDimensions = (): WindowDimensions => {
  const [windowDimensions, setWindowDimensions] = React.useState<WindowDimensions>({
    windowWidth: undefined,
    windowHeight: undefined
  })

  React.useEffect(() => {
    const handleResize = () => {
      setWindowDimensions({ windowWidth: window.innerWidth, windowHeight: window.innerHeight })
    }
    window.addEventListener('resize', handleResize)
    handleResize()
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  return windowDimensions
}

interface ElementDimensions {
  clientWidth: number
  clientHeight: number
  scrollWidth: number
  scrollHeight: number
}

const styles: UprightStyle = {
  textContainer: { display: 'grid', gridTemplateColumns: '1fr' },
  label: { textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }
}

interface OverflowCellProps {
  style?: React.CSSProperties
  label: JSX.Element | string
  tooltipContent: JSX.Element | string
}

export const TextWithOverflowTooltip: React.FC<OverflowCellProps> = ({ style, label, tooltipContent }) => {
  const [textOverflowing, setTextOverflowing] = React.useState(false)
  const ref = React.useRef<HTMLSpanElement>()
  const { windowWidth, windowHeight } = useWindowDimensions()
  const currentRef = ref?.current
  const { clientWidth, scrollWidth, clientHeight, scrollHeight } = React.useMemo((): ElementDimensions => {
    return {
      clientWidth: currentRef?.clientWidth,
      scrollWidth: currentRef?.scrollWidth,
      clientHeight: currentRef?.clientHeight,
      scrollHeight: currentRef?.scrollHeight
    }
  }, [currentRef?.clientWidth, currentRef?.scrollWidth, currentRef?.clientHeight, currentRef?.scrollHeight])

  React.useEffect(() => {
    setTextOverflowing(checkOverflow(clientWidth, scrollWidth, clientHeight, scrollHeight, windowWidth, windowHeight))
  }, [clientWidth, scrollWidth, clientHeight, scrollHeight, windowWidth, windowHeight])

  return (
    <div style={style}>
      <Tooltip content={tooltipContent} disabled={!textOverflowing}>
        <div style={styles.textContainer}>
          <span ref={ref} style={styles.label}>
            {label}
          </span>
        </div>
      </Tooltip>
    </div>
  )
}
